import { AlertCircle } from 'lucide-react';
import { ApiError } from '@/lib/errors';
import { cn } from '@/lib/utils';

interface Props {
  /** Error from a mutation / query. Most useful when it's an ApiError. */
  error: unknown;
  /** Optional extra class on the outer wrapper. */
  className?: string;
}

/**
 * Standard form-error block that renders BE per-field validation
 * reasons (from `ApiError.details`) — not just the generic
 * "Validation failed" toast. Use this anywhere a mutation may surface
 * an ApiError to the operator (forms, wizards, multi-step flows).
 *
 * Falls back to `err.message` when there's no structured detail; shows
 * nothing when `error` is null/undefined.
 *
 * See `feedback_validation_field_details.md` for the rationale.
 */
export function FormErrorBlock({ error, className }: Props) {
  if (!error) return null;
  const apiError = error instanceof ApiError ? error : null;
  const message = apiError?.message ?? (error instanceof Error ? error.message : undefined);
  const code = apiError?.code;
  const fieldDetails: Array<[string, string]> = [];
  const rawDetails = apiError?.details;
  if (rawDetails && typeof rawDetails === 'object' && !Array.isArray(rawDetails)) {
    for (const [field, reason] of Object.entries(rawDetails)) {
      if (typeof reason === 'string') fieldDetails.push([field, reason]);
      else if (Array.isArray(reason)) {
        for (const r of reason) {
          if (typeof r === 'string') fieldDetails.push([field, r]);
        }
      }
    }
  }
  if (!message && fieldDetails.length === 0) return null;

  const headline =
    code === 'Validation' && fieldDetails.length > 0
      ? 'Please fix the highlighted field' +
        (fieldDetails.length > 1 ? 's' : '') +
        ' below'
      : (code ?? 'Request failed');
  const subline =
    code === 'Validation' && fieldDetails.length > 0 ? undefined : message;

  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-2.5 rounded-md border border-destructive/25 bg-destructive/5 px-3 py-2.5 text-[12.5px] text-destructive',
        className,
      )}
    >
      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <div className="flex-1 space-y-1">
        <div className="font-semibold">{headline}</div>
        {subline && <div className="text-destructive/85">{subline}</div>}
        {fieldDetails.length > 0 && (
          <ul className="list-disc space-y-0.5 pl-4 text-destructive/85">
            {fieldDetails.map(([field, reason], i) => (
              <li key={`${field}-${i}`}>
                <span className="font-medium">{prettifyField(field)}:</span> {reason}
              </li>
            ))}
          </ul>
        )}
        {code === 'Validation' && fieldDetails.length === 0 && (
          <div className="text-destructive/85">
            One or more fields didn't pass validation. Re-check required
            fields (marked *) and inputs with format rules (GSTIN, PAN,
            IFSC, URLs).
          </div>
        )}
      </div>
    </div>
  );
}

function prettifyField(raw: string): string {
  return raw
    .replace(/[._]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
